import { Activity, Ban, TriangleAlert, Wrench } from 'lucide-react'
import { Suspense, use } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { SidebarStatusIndicatorFallback } from '@/components/layout/sidebar-status-indicator/fallback'
import { SidebarMenuButton, SidebarMenuItem } from '@/components/sv/sidebar'
import { capitalize, cn } from '@/lib/utils'

type StatusIndicator = 'none' | 'minor' | 'major' | 'critical' | 'maintenance'

type StatusPageResponse = {
  page: {
    id: string
    name: string
    url: string
    time_zone: string
    updated_at: string
  }
  status: {
    indicator: StatusIndicator
    description: string
  }
}

const pFetchSevallaStatus: Promise<StatusPageResponse | null> = fetch(
  'https://status.sevalla.com/api/v2/status.json'
)
  .then((res) => (res.ok ? res.json() : null))
  .catch(() => null)

const ICON_BY_INDICATOR: Record<StatusIndicator, typeof Activity> = {
  none: Activity,
  minor: Activity,
  major: TriangleAlert,
  critical: Ban,
  maintenance: Wrench,
}

const COLOR_BY_INDICATOR: Record<StatusIndicator, string> = {
  none: 'text-green-600',
  minor: 'text-amber-500',
  major: 'text-red-500',
  critical: 'text-red-700',
  maintenance: 'text-blue-500',
}

const SidebarStatusIndicatorData = () => {
  const data = use(pFetchSevallaStatus)
  const status = data?.status

  if (!status) {
    return null
  }

  const Icon = ICON_BY_INDICATOR[status.indicator]
  const colorClass = COLOR_BY_INDICATOR[status.indicator]
  const description = capitalize(status.description.toLowerCase())

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={description}>
        <a
          href="https://status.sevalla.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon className={cn(colorClass)} />
          <div className="overflow-hidden flex-1 transition-[max-width] duration-200 ease-linear max-w-40 group-data-[collapsible=icon]:max-w-0">
            <span className={cn('whitespace-nowrap', colorClass)}>
              {description}
            </span>
          </div>
        </a>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

export const SidebarStatusIndicator = () => (
  <ErrorBoundary fallback={null}>
    <Suspense fallback={<SidebarStatusIndicatorFallback />}>
      <SidebarStatusIndicatorData />
    </Suspense>
  </ErrorBoundary>
)
